import type { ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { faAngleDoubleLeft, faAngleDoubleRight } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { clsx } from 'clsx';

import { useSideBarContext } from '@spacefill/shared/src/utils/contexts/SideBarContext';

import { useMediaQuery } from '../../hooks/useMediaQuery';
import { safeLocalStorage } from '../../utils/storage';
import type { NavItemProps } from './NavItem';
import { NavItem } from './NavItem';
import { SidebarSection } from './SidebarSection';
import logoSpacefill from './logo-spacefill.svg';

export type NavItemConfig = {
    subItems?: NavItemProps[];
} & NavItemProps;

export type NavSectionConfig = {
    sectionTitle?: string;
    sectionDataTestid?: string;
    items: NavItemConfig[];
};

function isSectionedNavigation(nav: NavItemConfig[] | NavSectionConfig[]): nav is NavSectionConfig[] {
    return nav.length > 0 && 'items' in nav[0];
}

export interface SidebarProps {
    mainNavigation?: NavItemConfig[] | NavSectionConfig[];
    supportNavigation?: NavItemConfig[];
    bottomNavigation?: NavItemConfig[];
    profileTopNavigation?: NavItemConfig[];
    profileNavigation?: NavItemConfig[];
    staffDebugTools?: ReactNode;
    logoUrl?: string | null;
    dataTestid?: string;
}

function renderNavItem(navItem: NavItemConfig, idx: number, isSizeBarCollapsed: boolean, isMouseInSidebar?: boolean) {
    return (
        <NavItem
            key={`nav-item-${idx}`}
            title={isSizeBarCollapsed ? '' : navItem.title}
            icon={navItem.icon}
            hasRoundedIcon={navItem.hasRoundedIcon}
            bottomDivider={navItem.bottomDivider}
            logoUrl={navItem.logoUrl ?? ''}
            countValue={navItem.countValue}
            isProFeature={navItem.isProFeature}
            to={navItem.to}
            isActive={navItem.isActive}
            href={navItem.href}
            target={navItem.target}
            onClick={navItem.onClick}
            dataTestid={navItem.dataTestid}
            isNew={navItem.isNew}
            isMouseInSidebar={isMouseInSidebar}
        />
    );
}

export const Sidebar = ({
    mainNavigation = [],
    bottomNavigation = [],
    staffDebugTools = null,
    logoUrl = null,
    dataTestid,
}: SidebarProps) => {
    const [isHovered, setIsHovered] = useState(false);
    const isSmallScreen = useMediaQuery('sm');
    const { isSizeBarCollapsed = isSmallScreen, setIsSizeBarCollapsed } = useSideBarContext();
    const { t } = useTranslation();

    // Glider state
    const navContainerRef = useRef<HTMLUListElement>(null);
    const activeItemRef = useRef<HTMLLIElement | null>(null);
    const hoveredItemRef = useRef<HTMLLIElement | null>(null);
    const [glider, setGlider] = useState<{
        top: number;
        left: number;
        width: number;
        height: number;
        visible: boolean;
        isMouseInSidebar: boolean;
        isActiveItem: boolean;
    }>({
        top: 0,
        left: 0,
        width: 0,
        height: 0,
        visible: false,
        isMouseInSidebar: false,
        isActiveItem: false,
    });

    const positionGliderOn = useCallback((element: HTMLLIElement, isActive: boolean) => {
        const container = navContainerRef.current;
        if (!container) return;
        const containerRect = container.getBoundingClientRect();
        const itemRect = element.getBoundingClientRect();
        setGlider({
            top: itemRect.top - containerRect.top + container.scrollTop,
            left: itemRect.left - containerRect.left,
            width: itemRect.width,
            height: itemRect.height,
            visible: true,
            isMouseInSidebar: true,
            isActiveItem: isActive,
        });
    }, []);

    // Single mousemove handler on the <ul> container
    const handleNavMouseMove = useCallback(
        (e: React.MouseEvent) => {
            const target = (e.target as HTMLElement).closest<HTMLLIElement>('[data-component-name="NavItem"]');
            if (target) {
                // Only update if we moved to a different item
                if (hoveredItemRef.current !== target) {
                    hoveredItemRef.current = target;
                    const isActive = target.getAttribute('data-active') === 'true';
                    if (isActive) {
                        activeItemRef.current = target;
                    }
                    positionGliderOn(target, isActive);
                }
            } else {
                // Mouse is on a gap / section header → hide glider
                if (hoveredItemRef.current) {
                    hoveredItemRef.current = null;
                    setGlider((prev) => ({ ...prev, visible: false }));
                }
            }
        },
        [positionGliderOn]
    );

    const handleNavMouseLeave = useCallback(() => {
        hoveredItemRef.current = null;
        setGlider((prev) => ({ ...prev, visible: false }));
    }, []);

    useEffect(() => {
        setIsSizeBarCollapsed(isSmallScreen);
    }, [isSmallScreen, setIsSizeBarCollapsed]);

    const handleResizeSidebar = () => {
        setIsSizeBarCollapsed(!isSizeBarCollapsed);
        safeLocalStorage.setItem('isSizeBarCollapsed', JSON.stringify(!isSizeBarCollapsed));
    };

    let hoverTimeout: NodeJS.Timeout | null = null;

    const handleMouseEnter = () => {
        if (hoverTimeout) clearTimeout(hoverTimeout); //
        setIsHovered(true);
        setGlider((prev) => ({ ...prev, isMouseInSidebar: true }));
    };

    const handleMouseLeave = () => {
        hoverTimeout = setTimeout(() => {
            setIsHovered(false);
            setGlider((prev) => ({ ...prev, isMouseInSidebar: false }));
        }, 30);
    };

    return (
        <nav
            role='navigation'
            className='relative z-sidebar flex h-full flex-col bg-sidebar-background transition-all'
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            data-component-name='Sidebar'
        >
            {isHovered && (
                <button
                    className='cursor-pointer'
                    aria-label='Toggle sidebar size'
                >
                    <FontAwesomeIcon
                        className='absolute right-2 top-2 p-1 text-sm text-sidebar-foreground bg-sidebar-background'
                        icon={isSizeBarCollapsed ? faAngleDoubleRight : faAngleDoubleLeft}
                        onClick={handleResizeSidebar}
                    />
                </button>
            )}
            <div
                className='overflow-y-auto bg-sidebar-background'
                // Force dark mode on the sidebar to have a black scrollbar
                style={{ colorScheme: 'dark' }}
            >
                <div
                    className={clsx(
                        `pt-3 px-4 mb-2 overflow-hidden flex flex-col transition-all content-center items-center`,
                        isSizeBarCollapsed ? 'w-sidebar-collapsed-logo ml-2' : 'w-full'
                    )}
                >
                    <Link
                        className={clsx('max-w-full')}
                        title={t('Return to home')}
                        to='/'
                    >
                        <img
                            src={logoUrl || logoSpacefill}
                            alt=''
                            className='max-w-full max-h-12.5 object-contain'
                        />
                    </Link>

                    {!isSizeBarCollapsed && logoUrl && (
                        <div className='flex my-2 items-center gap-1 text-[0.6rem] whitespace-nowrap text-sidebar-foreground justify-center px-2'>
                            Powered by
                            <div className='w-17.5'>
                                <svg
                                    xmlns='http://www.w3.org/2000/svg'
                                    width='60'
                                    viewBox='0 0 139 24'
                                >
                                    <path
                                        d='M68.037 5.914a6.163 6.163 0 0 0-3.803 1.218v-.938h-2.64V24h2.644v-6.82c.99.76 2.283 1.216 3.804 1.216.484 0 .965-.059 1.434-.175 2.657-.656 4.632-3.1 4.632-6.062-.008-3.48-2.722-6.245-6.07-6.245Zm1.48 9.706a3.707 3.707 0 0 1-1.86.488 3.983 3.983 0 0 1-3.423-1.775V9.97c.346-.51.802-.936 1.335-1.248a4.13 4.13 0 0 1 2.088-.526c2.206 0 3.806 1.825 3.806 3.956a3.981 3.981 0 0 1-1.946 3.466ZM56.19 11.19l-2.23-.355c-1.396-.23-1.853-.838-1.853-1.472 0-.836.838-1.395 2.36-1.395 1.52 0 2.663.508 2.79 1.801h2.434c-.253-3.12-2.916-3.904-5.225-3.904-2.967 0-4.971 1.418-4.971 3.726 0 2.156 1.7 3.068 3.527 3.372l2.637.457c1.015.177 1.593.685 1.593 1.395 0 .938-1.065 1.444-2.612 1.444-1.802 0-2.893-.608-3.172-2.07l-2.434-.025c.304 3.143 2.708 4.26 5.53 4.26 2.993 0 5.199-1.293 5.199-3.804.003-2.316-1.771-3.1-3.573-3.43ZM135.875.306v17.802h2.638V.306h-2.638ZM85.412 7.132c-1.015-.786-2.308-1.242-3.823-1.242-3.345 0-6.037 2.788-6.037 6.263 0 3.474 2.689 6.263 6.037 6.263a6.141 6.141 0 0 0 3.823-1.242v.934h2.613V6.191h-2.613v.941Zm0 7.175c-.887 1.274-2.13 1.725-3.45 1.725-2.181 0-3.779-1.752-3.779-3.88 0-2.127 1.593-3.88 3.779-3.88 1.32 0 2.563.457 3.45 1.725v4.31Zm34.613-9.514c0-1.546 1.014-2.51 2.561-2.51.614.023 1.225.099 1.826.228l.38-2.13a9.464 9.464 0 0 0-2.51-.381c-2.816 0-4.87 1.826-4.87 4.793v13.315h2.613V8.395h5.325v9.713h2.612V6.191h-7.937V4.793ZM109.584 5.89c-3.627 0-6.137 2.737-6.137 6.263 0 3.55 2.536 6.263 6.291 6.263 2.586 0 4.692-1.274 5.452-3.424h-2.891c-.479.761-1.37 1.115-2.536 1.115-2.231 0-3.374-1.37-3.628-3.17h9.359c.516-4.031-1.952-7.047-5.91-7.047Zm-3.4 5.223c.306-1.674 1.448-2.992 3.425-2.992 1.853 0 3.121 1.04 3.273 2.992h-6.698Zm24.415 6.998h2.638V.306h-2.638V18.11ZM96.167 8.272c1.592 0 2.79.914 3.12 2.258h2.689c-.432-2.739-2.816-4.64-5.81-4.64-3.576 0-6.29 2.737-6.29 6.263 0 3.55 2.708 6.263 6.29 6.263 2.994 0 5.378-1.902 5.81-4.64h-2.692c-.33 1.344-1.523 2.256-3.12 2.256-2.207 0-3.653-1.725-3.653-3.88 0-2.154 1.45-3.88 3.656-3.88ZM19.887 12.146A15.561 15.561 0 0 0 25.333.307h-9.717a5.882 5.882 0 0 1-5.884 5.884H0v9.713h9.732a15.536 15.536 0 0 0 10.14-3.746 15.564 15.564 0 0 0-5.444 11.839h9.717a5.881 5.881 0 0 1 5.884-5.882h9.732V8.4h-9.732a15.537 15.537 0 0 0-10.142 3.745Z'
                                        fill='currentColor'
                                    />
                                </svg>
                            </div>
                        </div>
                    )}
                </div>

                {staffDebugTools && <div className='p-2'>{staffDebugTools}</div>}
                <ul
                    ref={navContainerRef}
                    className='relative flex flex-col bg-sidebar-background pr-2'
                    onMouseMove={handleNavMouseMove}
                    onMouseLeave={handleNavMouseLeave}
                >
                    <div
                        className={clsx(
                            'pointer-events-none absolute rounded-md',
                            glider.isActiveItem ? 'bg-button-background/30' : 'bg-sidebar-foreground/5'
                        )}
                        style={{
                            top: glider.top,
                            left: glider.left,
                            width: glider.width,
                            height: glider.height,
                            opacity: glider.visible ? 1 : 0,
                            transition: glider.visible
                                ? 'top 0.2s cubic-bezier(0.4, 0, 0.2, 1), left 0.15s cubic-bezier(0.4, 0, 0.2, 1), width 0.15s cubic-bezier(0.4, 0, 0.2, 1), height 0.15s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.15s ease'
                                : 'opacity 0.15s ease',
                        }}
                    />
                    {mainNavigation.length > 0 && isSectionedNavigation(mainNavigation)
                        ? mainNavigation.map((section, sectionIdx) => {
                              const visibleItems = section.items.filter((item) => {
                                  if (!item.conditionForDisplay) {
                                      return true;
                                  }
                                  const condition = item.conditionForDisplay();
                                  if (typeof condition !== 'boolean') {
                                      console.error(
                                          `conditionForDisplay for nav item "${item.title}" did not return a boolean value. Please ensure it returns true or false.`
                                      );
                                      return false;
                                  }
                                  return condition;
                              });
                              if (visibleItems.length === 0) return null;
                              return (
                                  <SidebarSection
                                      key={`section-${sectionIdx}`}
                                      title={section.sectionTitle}
                                      isCollapsed={isSizeBarCollapsed}
                                      dataTestid={section.sectionDataTestid}
                                  >
                                      {visibleItems.map((navItem, idx) =>
                                          renderNavItem(navItem, idx, isSizeBarCollapsed, glider.isMouseInSidebar)
                                      )}
                                  </SidebarSection>
                              );
                          })
                        : (mainNavigation as NavItemConfig[]).map((navItem, idx) => {
                              if (navItem.conditionForDisplay?.() === false) {
                                  return null;
                              }
                              return renderNavItem(navItem, idx, isSizeBarCollapsed, glider.isMouseInSidebar);
                          })}
                </ul>
            </div>

            <div className='mt-auto border-t border-solid border-t-zinc-600 pt-2'>
                <ul className='bg-sidebar-background px-2 pb-2'>
                    {bottomNavigation.map((navItem, idx) => (
                        <NavItem
                            key={`nav-item-${idx}`}
                            title={isSizeBarCollapsed ? '' : navItem.title}
                            logoUrl={navItem.logoUrl ?? ''}
                            icon={navItem.icon}
                            hasRoundedIcon={navItem.hasRoundedIcon}
                            bottomDivider={navItem.bottomDivider}
                            countValue={navItem.countValue}
                            isProFeature={navItem.isProFeature}
                            isActive={navItem.isActive}
                            to={navItem.to}
                            href={navItem.href}
                            target={navItem.target}
                            onClick={navItem.onClick}
                            dataTestid={dataTestid && `${dataTestid}-title`}
                            className={navItem.className}
                        >
                            {navItem.subItems?.length && (
                                <ul
                                    data-testid='sidebar-navitem'
                                    className='origin-top rounded-md transition'
                                >
                                    {navItem.subItems.map((subItem: NavItemProps, subIdx) => (
                                        <NavItem
                                            key={`subnav-item-${idx}-subitem-${subIdx}`}
                                            title={subItem.title}
                                            icon={subItem.icon}
                                            hasRoundedIcon={subItem.hasRoundedIcon}
                                            bottomDivider={navItem.bottomDivider}
                                            isProFeature={subItem.isProFeature}
                                            countValue={subItem.countValue}
                                            isActive={subItem.isActive}
                                            to={subItem.to}
                                            href={subItem.href}
                                            target={subItem.target}
                                            onClick={subItem.onClick}
                                            dataTestid={subItem.dataTestid}
                                            isSubNav
                                            className={clsx('mb-1', navItem.className)}
                                        />
                                    ))}
                                </ul>
                            )}
                        </NavItem>
                    ))}
                </ul>
            </div>
        </nav>
    );
};
